import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import * as scanner from '../../platform/scanner';
import { useAuth } from '../auth/AuthContext';
import { ComponentPicker } from '../components/ComponentPicker';
import type { ComponentBrief } from '../components/api';
import { bindBarcode, resolveScan, type ScanMatch, type ScanResult } from './api';
import { printerProfileIdFromUrl } from './incoming';

/**
 * Point the camera at a label, or type the code off it.
 *
 * Both routes go through the same endpoint, because the person holding the phone
 * does not know or care whether they are looking at one of our QR labels, a
 * manufacturer's barcode, or a code they have to read out because the label is
 * scuffed. The interesting screen is the third one: a barcode nobody has ever
 * seen before, which is how an unfamiliar reel gets into the catalogue.
 */
export function ScanPage({ onOpen, onPrinterProfile, initial }: {
  onOpen: (match: ScanMatch) => void;
  /** A calibration QR is not a catalogue barcode: take it to its importer. */
  onPrinterProfile: (id: string) => void;
  /** A code from a QR that was opened outside the app — see App.tsx. */
  initial?: string | null;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();

  const video = useRef<HTMLVideoElement>(null);
  const session = useRef<scanner.Session | null>(null);

  const [live, setLive] = useState(false);
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [binding, setBinding] = useState(false);
  const [picked, setPicked] = useState<ComponentBrief | null>(null);

  const support = scanner.cameraSupport();

  const lookUp = async (value: string, symbology?: string) => {
    if (!value.trim()) return;
    const printerProfile = printerProfileIdFromUrl(value);
    if (printerProfile) {
      stop();
      onPrinterProfile(printerProfile);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const found = await resolveScan(value, symbology);
      setResult(found);
      setBinding(false);
      // A hit is what the user wanted; going straight to the record saves the
      // extra tap that a "found it, open?" step would cost on every single scan.
      if (found.resolved && found.match) {
        stop();
        onOpen(found.match);
      }
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.code : 'unknown');
    } finally {
      setBusy(false);
    }
  };

  const bind = async () => {
    if (!picked || !result) return;
    setBusy(true);
    try {
      await bindBarcode(picked.id, {
        value: result.value,
        symbology: result.symbology ?? 'unknown',
        source: 'scan',
      });
      // Cleared rather than left showing: the barcode now resolves, and leaving
      // the "not found" panel up would say otherwise.
      setBinding(false);
      setPicked(null);
      setResult(null);
      setTyped('');
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.code : 'unknown');
    } finally {
      setBusy(false);
    }
  };

  const stop = () => {
    session.current?.stop();
    session.current = null;
    setLive(false);
  };

  const startCamera = async () => {
    setError(null);
    if (!video.current) return;
    try {
      session.current = await scanner.start(video.current, (reading) => {
        void lookUp(reading.value, reading.symbology);
      });
      // In the application the reader has already closed by the time this
      // resolves, so there is nothing to offer to switch off.
      setLive(!scanner.oneShot);
    } catch (exc) {
      setError(`camera.${(exc as Error).message}`);
    }
  };

  // Opening the Scan screen is itself the explicit user action that authorises
  // the camera. Starting immediately avoids a redundant second tap on phones.
  useEffect(() => {
    if (support === 'ok') void startCamera();
    return () => session.current?.stop();
    // startCamera only closes over the stable refs and the current screen state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [handled, setHandled] = useState<string | null>(null);
  useEffect(() => {
    if (initial && initial !== handled) {
      setHandled(initial);
      setTyped(initial);
      void lookUp(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const unresolved = result && !result.resolved;
  const canBind = can('barcode.link');

  return (
    <div className="scan">
      <h2>{t('scan.title')}</h2>

      <div className="scan__camera">
        {/* Kept mounted even when idle: starting the stream needs the element to
            already exist, and creating it on demand costs a frame of black. */}
        <video ref={video} className={live ? 'scan__video is-live' : 'scan__video'} muted />
        {live && (
          <button type="button" onClick={stop}>
            {t('scan.stop')}
          </button>
        )}
        {support !== 'ok' && <p className="hint">{t(`scan.camera.${support}`)}</p>}
      </div>

      <form
        className="scan__manual"
        onSubmit={(event) => {
          event.preventDefault();
          void lookUp(typed);
        }}
      >
        <label>
          {t('scan.manual')}
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder="C-AHYD15"
            autoComplete="off"
            // Codes are upper-case and typed on a phone. Both hints matter more
            // than they look: autocapitalise off gives lower-case input the
            // resolver folds anyway, and inputMode keeps the numeric row visible.
            autoCapitalize="characters"
            spellCheck={false}
          />
        </label>
        <button type="submit" disabled={busy || !typed.trim()}>
          {t('scan.find')}
        </button>
      </form>

      {error && <p className="error">{t(`scan.errors.${error}`, t('scan.errors.unknown'))}</p>}

      {unresolved && (
        <div className="scan__unresolved">
          <p>
            <strong>{t('scan.notFound')}</strong> <code>{result.value}</code>
            {result.symbology && <span className="hint"> · {result.symbology}</span>}
          </p>

          {result.suggestions.length > 0 && (
            <div className="scan__suggestions">
              {/* This is what stands in for a check character in the code: rather
                  than "invalid", the record a single mistyped character away. */}
              <p>{t('scan.didYouMean')}</p>
              <ul>
                {result.suggestions.map((item) => (
                  <li key={item.id}>
                    <button type="button" onClick={() => onOpen(item)}>
                      <code>{item.code}</code> {item.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canBind && !binding && (
            <button type="button" onClick={() => setBinding(true)}>
              {t('scan.bind')}
            </button>
          )}

          {canBind && binding && (
            <div className="scan__bind">
              <p className="hint">{t('scan.bindHint')}</p>
              <ComponentPicker
                label={t('scan.bindPick')}
                value={picked}
                onChange={setPicked}
              />
              <button type="button" disabled={!picked || busy} onClick={() => void bind()}>
                {t('scan.bindConfirm')}
              </button>
              <button type="button" onClick={() => { setBinding(false); setPicked(null); }}>
                {t('common.cancel')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
